import { KeyedSet, memone } from '@deqo/foundation';
import Fuse from 'fuse.js';
import { injectable } from 'inversify';
import { Connector } from '../../app/Container';
import { CommandDefinition } from './CommandDefinition';

@injectable()
export class CommandRegistry implements Iterable<CommandDefinition<any>> {

    private cmds = new KeyedSet<CommandDefinition<any>>(x => x.name);

    constructor(private readonly parent?: CommandRegistry) {
    }

    public add<P>(def: CommandDefinition<P>): CommandRegistry {
        if (this.cmds.get(def.name)) {
            throw new Error(`Command with name already registered: ${def.name}`);
        }
        this.cmds.add(def);
        return this;
    }

    public remove(name: string) {
        this.cmds.delete(name);
        return this;
    }

    public get<T = unknown>(name: string): CommandDefinition<T> | null {
        return this.cmds.get(name) ?? this.parent?.get(name) ?? null;
    }

    public filter(predicate: (cmd: CommandDefinition<any>) => boolean): CommandDefinition<any>[] {
        return this.cmds.array.filter(predicate);
    }

    public suggest(input?: string | null, includeAll?: boolean): CommandDefinition<any>[] {
        input = normalize(input || '');
        const list = this.getSortedCombinedList(this.parent?.cmds?.array, this.cmds.array)
            .filter(x => !!x.wizard || includeAll);
        const filtered = !!input
            ? new Fuse(list, { includeScore: true, keys: ['name', 'label'] }).search(input).filter(x => x.score < 0.1).map(x => x.item)
            : list;
        return filtered;
    }

    public [Symbol.iterator](): Iterator<CommandDefinition<any>, any, undefined> {
        return this.cmds.array[Symbol.iterator]();
    }

    @memone()
    private getSortedCombinedList(parentList?: CommandDefinition<any>[], localList?: CommandDefinition<any>[]) {
        return (parentList ?? []).concat(localList ?? [])
            .filter(x => !!x.label?.trim())
            .sort((a, b) => (a.label).localeCompare(b.label));
    }
}

export const inCommands = (...defs: CommandDefinition<any>[]): Connector =>
    r => void (defs.forEach(d => r.call(CommandRegistry).add(d)));

const normalize = (input: string) => input.toLowerCase().replace(/\s+/g, '');

const matcher = (input: string, includeAll: boolean) => {
    return (def: CommandDefinition<any>) => {
        return (normalize(def.label).includes(input) || !input) && (!!def.wizard || includeAll);
    };
};

